/* ==========================================================================
   Admin Access Requests Management Page
   Manage provider access requests (approve / deny)
   ========================================================================== */

import { useState, useEffect, useMemo, useCallback, useRef } from 'react'
import { Search, CheckCircle, XCircle, ChevronDown } from 'lucide-react'
import { Card } from '@/components/ui/Card'
import { Button } from '@/components/ui/Button'
import { Input } from '@/components/ui/Input'
import { Modal } from '@/components/ui/Modal'
import { Textarea } from '@/components/ui/Textarea'
import { AdminTableSkeleton } from '@/components/skeletons'
import { useAdminAuth } from '@/hooks/useAdminAuth'
import { useToast } from '@/context/ToastContext'
import {
  listAccessRequests,
  approveAccessRequest,
  denyAccessRequest,
} from '@/api/admin'
import { ApiClientError } from '@/api/client'
import { AccessRequestStatus } from '@/api/types'
import type { AccessRequestResponse, AccessRequestListResponse } from '@/api/types'
import { isCanada, regionLabel } from '@/utils/countries'

/* --------------------------------------------------------------------------
   Constants
   -------------------------------------------------------------------------- */

const ITEMS_PER_PAGE = 10

type StatusTab = 'all' | AccessRequestStatus

const STATUS_TABS: { value: StatusTab; label: string }[] = [
  { value: 'all', label: 'All' },
  { value: AccessRequestStatus.PENDING, label: 'Pending' },
  { value: AccessRequestStatus.APPROVED, label: 'Approved' },
  { value: AccessRequestStatus.DENIED, label: 'Denied' },
]

/* --------------------------------------------------------------------------
   Status Badge Component
   -------------------------------------------------------------------------- */

/* --------------------------------------------------------------------------
   Message Cell — truncates long messages with an expand/collapse arrow
   (same pattern as the Product Catalog's categories cell)
   -------------------------------------------------------------------------- */

function MessageCell({ message }: { message: string }) {
  const [expanded, setExpanded] = useState(false)
  const [isOverflowing, setIsOverflowing] = useState(false)
  const textRef = useRef<HTMLSpanElement>(null)

  // Show the arrow only when the collapsed text actually overflows,
  // re-measuring on content changes and layout resizes.
  useEffect(() => {
    const el = textRef.current
    if (!el) return

    const measure = () => {
      // Only the truncated state can overflow horizontally; skip while
      // expanded so collapsing restores the correct arrow state.
      if (!expanded) {
        setIsOverflowing(el.scrollWidth > el.clientWidth)
      }
    }

    measure()

    if (typeof ResizeObserver === 'undefined') {
      window.addEventListener('resize', measure)
      return () => window.removeEventListener('resize', measure)
    }

    const observer = new ResizeObserver(measure)
    observer.observe(el)
    return () => observer.disconnect()
  }, [message, expanded])

  return (
    <div className="flex items-start gap-1">
      <span
        ref={textRef}
        className={
          expanded ? 'whitespace-normal break-words min-w-0' : 'truncate min-w-0'
        }
      >
        {message}
      </span>
      {(isOverflowing || expanded) && (
        <button
          onClick={() => setExpanded(!expanded)}
          className="shrink-0 p-0.5 mt-0.5 rounded text-gray-400 hover:text-deep-purple hover:bg-gray-100 transition-colors"
          aria-expanded={expanded}
          aria-label={expanded ? 'Collapse message' : 'Show full message'}
        >
          <ChevronDown
            className={`w-4 h-4 transition-transform ${expanded ? 'rotate-180' : ''}`}
          />
        </button>
      )}
    </div>
  )
}

function StatusBadge({ status }: { status: AccessRequestStatus }) {
  switch (status) {
    case AccessRequestStatus.PENDING:
      return (
        <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-yellow-100 text-yellow-800">
          Pending
        </span>
      )
    case AccessRequestStatus.APPROVED:
      return (
        <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800">
          Approved
        </span>
      )
    case AccessRequestStatus.DENIED:
      return (
        <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-gray-100 text-gray-600">
          Denied
        </span>
      )
    default:
      return null
  }
}

/* --------------------------------------------------------------------------
   Pagination Component
   -------------------------------------------------------------------------- */

interface PaginationProps {
  currentPage: number
  totalPages: number
  onPageChange: (page: number) => void
}

function Pagination({ currentPage, totalPages, onPageChange }: PaginationProps) {
  if (totalPages <= 1) return null

  const getPageNumbers = () => {
    const pages: (number | 'ellipsis')[] = []
    const showPages = 5

    if (totalPages <= showPages) {
      for (let i = 1; i <= totalPages; i++) {
        pages.push(i)
      }
    } else {
      if (currentPage <= 3) {
        for (let i = 1; i <= 4; i++) pages.push(i)
        pages.push('ellipsis')
        pages.push(totalPages)
      } else if (currentPage >= totalPages - 2) {
        pages.push(1)
        pages.push('ellipsis')
        for (let i = totalPages - 3; i <= totalPages; i++) pages.push(i)
      } else {
        pages.push(1)
        pages.push('ellipsis')
        pages.push(currentPage - 1)
        pages.push(currentPage)
        pages.push(currentPage + 1)
        pages.push('ellipsis')
        pages.push(totalPages)
      }
    }

    return pages
  }

  return (
    <div className="flex items-center justify-center gap-1 sm:gap-2">
      <button
        onClick={() => onPageChange(currentPage - 1)}
        disabled={currentPage === 1}
        className="px-3 py-1.5 text-sm text-charcoal hover:bg-gray-100 rounded-md disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
        aria-label="Previous page"
      >
        &larr;
      </button>

      {getPageNumbers().map((page, index) =>
        page === 'ellipsis' ? (
          <span key={`ellipsis-${index}`} className="px-2 py-1 text-gray-500">
            ...
          </span>
        ) : (
          <button
            key={page}
            onClick={() => onPageChange(page)}
            className={`px-3 py-1.5 text-sm rounded-md transition-colors ${
              page === currentPage
                ? 'bg-deep-purple text-white'
                : 'text-charcoal hover:bg-gray-100'
            }`}
            aria-label={`Page ${page}`}
            aria-current={page === currentPage ? 'page' : undefined}
          >
            {page}
          </button>
        )
      )}

      <button
        onClick={() => onPageChange(currentPage + 1)}
        disabled={currentPage === totalPages}
        className="px-3 py-1.5 text-sm text-charcoal hover:bg-gray-100 rounded-md disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
        aria-label="Next page"
      >
        &rarr;
      </button>
    </div>
  )
}

/* --------------------------------------------------------------------------
   Format Date Helper
   -------------------------------------------------------------------------- */

function formatDate(dateString: string): string {
  const date = new Date(dateString)
  return date.toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
  })
}

/* --------------------------------------------------------------------------
   Empty State Component
   -------------------------------------------------------------------------- */

function EmptyState() {
  return (
    <div className="text-center py-12">
      <div className="mx-auto w-16 h-16 bg-soft-lavender rounded-full flex items-center justify-center mb-4">
        <CheckCircle className="w-8 h-8 text-deep-purple" />
      </div>
      <h3 className="text-lg font-semibold text-charcoal mb-2">
        No access requests yet
      </h3>
      <p className="text-gray-600 max-w-sm mx-auto">
        When providers request access to your portal, their requests will appear here for review.
      </p>
    </div>
  )
}

/* --------------------------------------------------------------------------
   Main Component
   -------------------------------------------------------------------------- */

export function AccessRequests() {
  const { pharmacy } = useAdminAuth()
  // Canadian pharmacies have no NPI; license region is a province (PORTAL-162)
  const isCanadianPharmacy = isCanada(pharmacy?.country)
  const licenseRegionHeader = regionLabel(pharmacy?.country)
  const { success: showSuccess, error: showError } = useToast()
  const slug = pharmacy?.portal_slug || ''

  // Data state
  const [requests, setRequests] = useState<AccessRequestResponse[]>([])
  const [pendingCount, setPendingCount] = useState(0)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)

  // Search and filter state
  const [searchQuery, setSearchQuery] = useState('')
  const [statusFilter, setStatusFilter] = useState<StatusTab>('all')

  // Pagination state
  const [currentPage, setCurrentPage] = useState(1)

  // Approve confirmation state
  const [approveConfirmOpen, setApproveConfirmOpen] = useState(false)
  const [requestToApprove, setRequestToApprove] = useState<AccessRequestResponse | null>(null)
  const [isApproving, setIsApproving] = useState(false)

  // Deny modal state
  const [denyModalOpen, setDenyModalOpen] = useState(false)
  const [requestToDeny, setRequestToDeny] = useState<AccessRequestResponse | null>(null)
  const [denyReason, setDenyReason] = useState('')
  const [isDenying, setIsDenying] = useState(false)

  // Fetch access requests
  const fetchRequests = useCallback(async () => {
    if (!slug) return

    setLoading(true)
    setError(null)

    try {
      const response: AccessRequestListResponse = await listAccessRequests(slug)
      setRequests(response.requests)
      setPendingCount(response.pending_count)
    } catch (err) {
      const message =
        err instanceof ApiClientError
          ? err.message
          : 'Failed to load access requests. Please try again.'
      setError(message)
    } finally {
      setLoading(false)
    }
  }, [slug])

  useEffect(() => {
    fetchRequests()
  }, [fetchRequests])

  // Filter and search requests
  const filteredRequests = useMemo(() => {
    let result = requests

    // Filter by status
    if (statusFilter !== 'all') {
      result = result.filter((r) => r.status === statusFilter)
    }

    // Filter by search query
    if (searchQuery.trim()) {
      const query = searchQuery.toLowerCase()
      result = result.filter(
        (r) =>
          r.name.toLowerCase().includes(query) ||
          r.email.toLowerCase().includes(query)
      )
    }

    return result
  }, [requests, searchQuery, statusFilter])

  // Pagination
  const totalPages = Math.ceil(filteredRequests.length / ITEMS_PER_PAGE)
  const paginatedRequests = useMemo(() => {
    const start = (currentPage - 1) * ITEMS_PER_PAGE
    return filteredRequests.slice(start, start + ITEMS_PER_PAGE)
  }, [filteredRequests, currentPage])

  // Reset to page 1 when search/filter changes
  useEffect(() => {
    setCurrentPage(1)
  }, [searchQuery, statusFilter])

  // Open approve confirmation
  function handleApproveClick(request: AccessRequestResponse) {
    setRequestToApprove(request)
    setApproveConfirmOpen(true)
  }

  // Confirm approve
  async function handleConfirmApprove() {
    if (!requestToApprove) return

    setIsApproving(true)

    try {
      await approveAccessRequest(slug, requestToApprove.id)
      showSuccess(`Access request from ${requestToApprove.name} approved`)
      setApproveConfirmOpen(false)
      setRequestToApprove(null)
      await fetchRequests()
    } catch (err) {
      const message =
        err instanceof ApiClientError
          ? err.message
          : 'Failed to approve access request. Please try again.'
      showError(message)
    } finally {
      setIsApproving(false)
    }
  }

  // Open deny modal
  function handleDenyClick(request: AccessRequestResponse) {
    setRequestToDeny(request)
    setDenyReason('')
    setDenyModalOpen(true)
  }

  // Confirm deny
  async function handleConfirmDeny() {
    if (!requestToDeny) return

    setIsDenying(true)

    try {
      await denyAccessRequest(slug, requestToDeny.id, {
        reason: denyReason.trim() || null,
      })
      showSuccess(`Access request from ${requestToDeny.name} denied`)
      setDenyModalOpen(false)
      setRequestToDeny(null)
      setDenyReason('')
      await fetchRequests()
    } catch (err) {
      const message =
        err instanceof ApiClientError
          ? err.message
          : 'Failed to deny access request. Please try again.'
      showError(message)
    } finally {
      setIsDenying(false)
    }
  }

  return (
    <div className="p-6 sm:p-8">
      <div className="max-w-6xl">
        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-6">
          <div>
            <h1 className="font-serif text-2xl sm:text-3xl text-charcoal mb-6">Access Requests</h1>
            {pendingCount > 0 && !loading && (
              <p className="text-charcoal/70 mt-1">
                {pendingCount} pending request{pendingCount !== 1 ? 's' : ''} awaiting review
              </p>
            )}
          </div>
        </div>

        {/* Error Alert */}
        {error && (
          <div className="mb-6 p-4 bg-red-50 border border-red-200 rounded-md text-red-700 text-sm">
            {error}
            <button
              onClick={fetchRequests}
              className="ml-4 underline hover:no-underline"
            >
              Retry
            </button>
          </div>
        )}

        {/* Main Content */}
        <Card className="overflow-visible">
          {loading ? (
            <AdminTableSkeleton columns={isCanadianPharmacy ? 7 : 8} rows={5} />
          ) : requests.length === 0 ? (
            <EmptyState />
          ) : (
            <>
              {/* Status Filter Tabs + Search */}
              <div className="flex flex-col sm:flex-row gap-4 mb-6">
                {/* Tabs */}
                <div className="flex gap-1 bg-gray-100 p-1 rounded-lg">
                  {STATUS_TABS.map((tab) => (
                    <button
                      key={tab.value}
                      onClick={() => setStatusFilter(tab.value)}
                      className={`px-3 py-1.5 text-sm font-medium rounded-md transition-colors ${
                        statusFilter === tab.value
                          ? 'bg-white text-charcoal shadow-sm'
                          : 'text-gray-600 hover:text-charcoal'
                      }`}
                    >
                      {tab.label}
                      {tab.value === AccessRequestStatus.PENDING && pendingCount > 0 && (
                        <span className="ml-1.5 inline-flex items-center justify-center w-5 h-5 text-xs font-medium bg-yellow-100 text-yellow-800 rounded-full">
                          {pendingCount}
                        </span>
                      )}
                    </button>
                  ))}
                </div>

                {/* Search Input */}
                <div className="relative flex-1">
                  <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
                  <Input
                    type="text"
                    placeholder="Search by name or email..."
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    className="pl-10"
                  />
                </div>
              </div>

              {/* Results Summary */}
              <div className="text-sm text-gray-600 mb-4">
                Showing {paginatedRequests.length} of {filteredRequests.length} request{filteredRequests.length !== 1 ? 's' : ''}
                {searchQuery && ` matching "${searchQuery}"`}
              </div>

              {/* Requests Table */}
              {filteredRequests.length === 0 ? (
                <div className="text-center py-8 text-gray-500">
                  No access requests found matching your criteria.
                </div>
              ) : (
                <>
                  {/* Desktop Table */}
                  <div className="hidden sm:block">
                    <table className="w-full">
                      <thead>
                        <tr className="border-b border-gray-200">
                          <th className="text-left text-sm font-medium text-gray-600 pb-3">
                            Name
                          </th>
                          <th className="text-left text-sm font-medium text-gray-600 pb-3">
                            Email
                          </th>
                          {!isCanadianPharmacy && (
                            <th className="text-left text-sm font-medium text-gray-600 pb-3">
                              NPI
                            </th>
                          )}
                          <th className="text-left text-sm font-medium text-gray-600 pb-3">
                            {licenseRegionHeader}
                          </th>
                          <th className="text-left text-sm font-medium text-gray-600 pb-3">
                            Message
                          </th>
                          <th className="text-left text-sm font-medium text-gray-600 pb-3">
                            Status
                          </th>
                          <th className="text-left text-sm font-medium text-gray-600 pb-3">
                            Date
                          </th>
                          <th className="text-right text-sm font-medium text-gray-600 pb-3">
                            Actions
                          </th>
                        </tr>
                      </thead>
                      <tbody>
                        {paginatedRequests.map((request) => (
                          <tr
                            key={request.id}
                            className="border-b border-gray-100 last:border-b-0 hover:bg-gray-50 transition-colors"
                          >
                            <td className="py-4 pr-4">
                              <div className="text-charcoal font-medium">
                                {request.name}
                              </div>
                              {request.phone && (
                                <div className="text-sm text-gray-500 mt-0.5">
                                  {request.phone}
                                </div>
                              )}
                            </td>
                            <td className="py-4 pr-4 text-gray-600">
                              {request.email}
                            </td>
                            {!isCanadianPharmacy && (
                              <td className="py-4 pr-4">
                                {request.npi ? (
                                  <code className="text-sm bg-gray-100 px-2 py-0.5 rounded">
                                    {request.npi}
                                  </code>
                                ) : (
                                  <span className="text-gray-400">&mdash;</span>
                                )}
                              </td>
                            )}
                            <td className="py-4 pr-4 text-gray-600">
                              {request.license_state || (
                                <span className="text-gray-400">&mdash;</span>
                              )}
                            </td>
                            <td className="py-4 pr-4 text-gray-600 text-sm max-w-[200px]">
                              {request.message ? (
                                <MessageCell message={request.message} />
                              ) : (
                                <span className="text-gray-400">&mdash;</span>
                              )}
                            </td>
                            <td className="py-4 pr-4">
                              <StatusBadge status={request.status} />
                            </td>
                            <td className="py-4 pr-4 text-gray-600 text-sm">
                              {formatDate(request.created_at)}
                            </td>
                            <td className="py-4 text-right">
                              {request.status === AccessRequestStatus.PENDING ? (
                                <div className="flex items-center justify-end gap-2">
                                  <Button
                                    variant="primary"
                                    size="sm"
                                    onClick={() => handleApproveClick(request)}
                                  >
                                    <CheckCircle className="w-3.5 h-3.5 mr-1" />
                                    Approve
                                  </Button>
                                  <Button
                                    variant="ghost"
                                    size="sm"
                                    onClick={() => handleDenyClick(request)}
                                  >
                                    <XCircle className="w-3.5 h-3.5 mr-1" />
                                    Deny
                                  </Button>
                                </div>
                              ) : (
                                <span className="text-sm text-gray-400">&mdash;</span>
                              )}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>

                  {/* Mobile Cards */}
                  <div className="sm:hidden space-y-4">
                    {paginatedRequests.map((request) => (
                      <div
                        key={request.id}
                        className="border border-gray-200 rounded-lg p-4"
                      >
                        <div className="flex items-start justify-between gap-2 mb-2">
                          <div className="font-medium text-charcoal">
                            {request.name}
                          </div>
                          <StatusBadge status={request.status} />
                        </div>
                        <div className="text-sm text-gray-600 mb-2">
                          {request.email}
                        </div>
                        <div className="flex flex-wrap gap-2 text-sm text-gray-600 mb-3">
                          {!isCanadianPharmacy && request.npi && (
                            <span className="bg-gray-100 px-2 py-0.5 rounded">
                              NPI: {request.npi}
                            </span>
                          )}
                          {request.license_state && (
                            <span className="bg-gray-100 px-2 py-0.5 rounded">
                              {request.license_state}
                            </span>
                          )}
                        </div>
                        <div className="text-xs text-gray-500 mb-3">
                          Submitted: {formatDate(request.created_at)}
                        </div>
                        {request.message && (
                          <div className="text-sm text-gray-600 mb-3 p-2 bg-gray-50 rounded">
                            {request.message}
                          </div>
                        )}
                        {request.status === AccessRequestStatus.PENDING && (
                          <div className="flex flex-wrap gap-2">
                            <Button
                              variant="primary"
                              size="sm"
                              onClick={() => handleApproveClick(request)}
                            >
                              <CheckCircle className="w-3.5 h-3.5 mr-1" />
                              Approve
                            </Button>
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => handleDenyClick(request)}
                            >
                              <XCircle className="w-3.5 h-3.5 mr-1" />
                              Deny
                            </Button>
                          </div>
                        )}
                      </div>
                    ))}
                  </div>

                  {/* Pagination */}
                  {totalPages > 1 && (
                    <div className="mt-6 pt-4 border-t border-gray-200">
                      <Pagination
                        currentPage={currentPage}
                        totalPages={totalPages}
                        onPageChange={setCurrentPage}
                      />
                    </div>
                  )}
                </>
              )}
            </>
          )}
        </Card>
      </div>

      {/* Approve Confirmation Modal */}
      <Modal
        open={approveConfirmOpen}
        onClose={() => setApproveConfirmOpen(false)}
        title="Approve Access Request"
        size="sm"
      >
        <p className="text-gray-600 mb-6">
          Are you sure you want to approve the access request from{' '}
          <strong className="text-charcoal">
            {requestToApprove?.name}
          </strong>
          ? They will be invited to create a provider account.
        </p>
        <div className="flex flex-col sm:flex-row gap-3">
          <Button
            variant="secondary"
            onClick={() => setApproveConfirmOpen(false)}
            disabled={isApproving}
            className="sm:flex-1"
          >
            Cancel
          </Button>
          <Button
            variant="primary"
            onClick={handleConfirmApprove}
            loading={isApproving}
            className="sm:flex-1"
          >
            Approve Request
          </Button>
        </div>
      </Modal>

      {/* Deny Modal with Optional Reason */}
      <Modal
        open={denyModalOpen}
        onClose={() => setDenyModalOpen(false)}
        title="Deny Access Request"
        size="sm"
      >
        <p className="text-gray-600 mb-4">
          Are you sure you want to deny the access request from{' '}
          <strong className="text-charcoal">
            {requestToDeny?.name}
          </strong>
          ?
        </p>
        <div className="mb-6">
          <label
            htmlFor="deny-reason"
            className="block text-sm font-medium text-charcoal mb-1.5"
          >
            Reason (optional)
          </label>
          <Textarea
            id="deny-reason"
            placeholder="Provide a reason for denial..."
            value={denyReason}
            onChange={(e) => setDenyReason(e.target.value)}
            rows={3}
          />
        </div>
        <div className="flex flex-col sm:flex-row gap-3">
          <Button
            variant="secondary"
            onClick={() => setDenyModalOpen(false)}
            disabled={isDenying}
            className="sm:flex-1"
          >
            Cancel
          </Button>
          <Button
            variant="destructive"
            onClick={handleConfirmDeny}
            loading={isDenying}
            className="sm:flex-1"
          >
            Deny Request
          </Button>
        </div>
      </Modal>
    </div>
  )
}

export default AccessRequests
